import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {useImperativeHandle, useRef, useState} from 'react'
import {getItem, setItem} from '@github-ui/safe-storage/session-storage'
import {IDS} from '@github-ui/commenting/DomElements'
import {clsx} from 'clsx'
import styles from './CommentsSidePanel.module.css'
import {IssueCommentComposer, type IssueCommentComposerHandle} from '../comments/IssueCommentComposer'

const collapseStateLocalStorageKey = 'pullRequestCommentsPanelFooterCollapseState'

export interface CommentsSidePanelFooterHandle {
  /** Reveals the composer if it is collapsed and inserts the quoted text into the draft. */
  quoteReply: (quotedText: string) => void
}

export interface CommentsSidePanelFooterProps {
  ref?: React.Ref<CommentsSidePanelFooterHandle>
}

export function CommentsSidePanelFooter({ref}: CommentsSidePanelFooterProps) {
  // TODO: if this becomes pinnable, we will have to come up with an SSR safe strategy that
  // avoids CLS. Might have to be a setting we store on the server and return in the payload
  const defaultCollapseState = getItem(collapseStateLocalStorageKey)
  const [isMinimized, setIsMinimized] = useState(defaultCollapseState === 'true')
  const composerRef = useRef<IssueCommentComposerHandle>(null)

  useImperativeHandle(
    ref,
    () => ({
      quoteReply(quotedText) {
        const wasMinimized = isMinimized
        if (wasMinimized) {
          setIsMinimized(false)
          setItem(collapseStateLocalStorageKey, 'false')
        }
        composerRef.current?.insertQuote(quotedText, {append: wasMinimized})
      },
    }),
    [isMinimized],
  )

  return (
    <div className={clsx(styles.SidePanelFooter, 'd-flex flex-column gap-2 width-full')} id={IDS.issueCommentComposer}>
      <h4 className="d-flex">
        <IconButton
          aria-label={isMinimized ? 'Open PR comment panel' : 'Close PR comment panel'}
          icon={isMinimized ? ChevronRightIcon : ChevronDownIcon}
          size="small"
          aria-expanded={!isMinimized}
          tooltipDirection="n"
          variant="invisible"
          className="mr-2"
          onClick={() => {
            const newState = !isMinimized
            setIsMinimized(newState)
            setItem(collapseStateLocalStorageKey, String(newState))
          }}
        />
        <span className="pb-2">Add a comment</span>
      </h4>
      <IssueCommentComposer ref={composerRef} collapsed={isMinimized} />
    </div>
  )
}
